type Translate = (key: string, params: Record<string, string>) => string;

interface ClinicSeoSource {
  display_title?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  show_address?: boolean | null;
  address?: string | null;
}

/**
 * Fills in a templated meta title and description for a clinic detail page
 * when the CMS has none. The CMS copies the display title into meta_title by
 * default, so a meta_title equal to the display title counts as empty.
 * Custom CMS values are kept. Returns a new object; the input is not changed.
 */
export const withClinicSeoFallback = <T extends ClinicSeoSource>(clinic: T, t: Translate): T => {
  const title = clinic?.display_title?.trim();
  if (!title) return clinic;

  const metaTitle = clinic.meta_title?.trim();
  const metaDescription = clinic.meta_description?.trim();
  // CMS addresses can span lines ("Lower Belgrave Street,\r\nLondon"); a description needs one line.
  const address = clinic.show_address ? clinic.address?.trim().replace(/,?\s*[\r\n]+\s*/g, ", ") : "";

  return {
    ...clinic,
    meta_title:
      metaTitle && metaTitle.toLowerCase() !== title.toLowerCase()
        ? clinic.meta_title
        : t("clinic.detail.meta_title", { title }),
    meta_description: metaDescription
      ? clinic.meta_description
      : address
        ? t("clinic.detail.meta_description_with_address", { title, address })
        : t("clinic.detail.meta_description", { title }),
  };
};
